import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { type IDropdownItem } from 'shared/ui'
import { ComplianceApi } from 'entities/Compliance'
import {
  type IResponseCreateNumbersLandlineError,
  type IParamsCreateNumbersLandline,
  type IResponseCreateNumbersLandlineErrorKey,
} from 'entities/Phone/api/types'
import type { Phone } from 'entities/Phone/model/Phone'

const maxZipLength = 5

export type IEnterAddressStoreConfig = {
  fetchNumbersLandline: (number: string) => Promise<Phone | { error: string }>
  createNumbersLandline: () => Promise<
    Phone | { error: string | IResponseCreateNumbersLandlineError }
  >
  getPhoneQuery: () => string | null
  nextStepByNumber: (number: number, query?: string) => void
  nextStepNumber: number
}

export class EnterAddressStore {
  private _name = ''
  private _streetAddress = ''
  private _city = ''
  private _state = ''
  private _zip_code = ''
  private _statesMap: Map<string, IDropdownItem> = new Map()
  private _loadingCreate = false
  private _error = ''
  private _errorFieldMap: Map<IResponseCreateNumbersLandlineErrorKey, string> = new Map()

  constructor(private _config: IEnterAddressStoreConfig) {
    makeAutoObservable(this)
  }

  get states() {
    return Array.from(this._statesMap.values())
  }

  get name() {
    return this._name
  }

  get streetAddress() {
    return this._streetAddress
  }

  get city() {
    return this._city
  }

  get state() {
    return this._state
  }

  get zip_code() {
    return this._zip_code
  }

  get nextDisabled() {
    return Boolean(
      !this._name ||
        !this._streetAddress ||
        !this._city ||
        !this._state ||
        !this._zip_code ||
        this._loadingCreate
    )
  }

  get nextLoading() {
    return Boolean(this._loadingCreate)
  }

  get payload(): Omit<
    IParamsCreateNumbersLandline,
    'phone' | 'extension' | 'inbox_number' | 'phone_to_replace' | 'landline_phone'
  > {
    return {
      city: this._city,
      friendly_name: this._name,
      is_zipwhip: false,
      state: this._state,
      street: this._streetAddress,
      zip: this._zip_code,
    }
  }

  get error() {
    return this._error
  }

  dispose = () => {
    this._name = ''
    this._streetAddress = ''
    this._city = ''
    this._state = ''
    this._zip_code = ''
    this._statesMap.clear()
    this._loadingCreate = false
    this._error = ''
  }

  setZipCode = (value: string) => {
    const valueZip = value.replace(/[^0-9]/g, '')

    if (valueZip.length > maxZipLength) return

    this._zip_code = valueZip
  }

  setState = (value: string) => {
    this._state = value
  }

  setName = (value: string) => {
    this._name = value
  }

  setStreetAddress = (value: string) => {
    this._streetAddress = value
  }

  setCity = (value: string) => {
    this._city = value
  }

  loadStates = async () => {
    try {
      const response = await ComplianceApi.getBpStates()
      const data = response.data

      data.forEach((item) => {
        this._statesMap.set(item.id, {
          id: item.id,
          label: item.name,
        })
      })
    } catch (e) {
      logger.error(e)
    }
  }

  init = async () => {
    await this.loadStates()
  }

  syncData = (phone: Phone) => {
    if (!phone.landline) {
      this._name = ''
      this._city = ''
      this._streetAddress = ''
      this._state = ''
      this._zip_code = ''

      return
    }

    this._name = phone.landline.landlineName
    this._city = phone.landline.city
    this._streetAddress = phone.landline.street_address
    this._state = phone.landline.state
    this._zip_code = phone.landline.zip_code
  }

  getErrorField = (value: IResponseCreateNumbersLandlineErrorKey) => {
    return this._errorFieldMap.get(value)
  }

  nextClickHandler = async () => {
    try {
      if (this._loadingCreate) return

      runInAction(() => {
        this._errorFieldMap.clear()
        this._error = ''
        this._loadingCreate = true
      })

      let data
      const phoneQuery = this._config.getPhoneQuery()

      if (phoneQuery) {
        data = await this._config.fetchNumbersLandline(`+1${phoneQuery}`)
      } else {
        data = await this._config.createNumbersLandline()
      }

      if ('error' in data) {
        if (typeof data.error === 'object') {
          const errors = Object.entries(data.error)

          errors.forEach(([k, v]) => {
            const key = k as IResponseCreateNumbersLandlineErrorKey

            if (v.length) {
              this._errorFieldMap.set(key, v[0])
            }
          })
        } else {
          this._error = data.error
        }

        return
      }

      this._config.nextStepByNumber(this._config.nextStepNumber, `?phone=${data.national_number}`)
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loadingCreate = false
      })
    }
  }
}
